import {
  BadgeCheck,
  Paperclip,
  ArrowLeftRight,
  Package,
  Pencil,
  Trash2,
} from "lucide-react";
import type { AccountCardDisplay } from "../types/accounts-wallets.types";
import {
  CURRENCY_BADGE_STYLES,
  DEFAULT_CURRENCY_BADGE,
  COUNTRY_ICON_COLORS,
  DEFAULT_ICON_COLOR,
  DIGITAL_ICON_BG,
} from "../constants/accounts-wallets.constants";
import useTranslations from "@/shared/lang/useTranslations";

type AccountCardProps = {
  account: AccountCardDisplay;
  onCardClick?: (id: string) => void;
  onEditClick?: (id: string) => void;
  onDeleteClick?: (id: string, name: string) => void;
};

function getAttachmentsLabel(
  count: number,
  t: (key: keyof import("@/shared/lang/interfaces/accountsWallets").AccountsWalletsTranslations, opts?: { count?: number }) => string
): string {
  if (count === 0) return t("attachments_none");
  if (count === 1) return t("attachments_one");
  return t("attachments_many", { count });
}

function getIconColorClass(account: AccountCardDisplay): string {
  if (account.kind === "digital") return "";
  const code = (account.countryCode ?? "").toUpperCase();
  return COUNTRY_ICON_COLORS[code] ?? DEFAULT_ICON_COLOR;
}

export const AccountCard = ({
  account,
  onCardClick,
  onEditClick,
  onDeleteClick,
}: AccountCardProps) => {
  const { t } = useTranslations("accountsWallets");
  const badgeStyle =
    CURRENCY_BADGE_STYLES[account.currency] ?? DEFAULT_CURRENCY_BADGE;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onCardClick?.(account.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onCardClick?.(account.id);
        }
      }}
      className="group bg-white dark:bg-zinc-900 rounded-xl border border-slate-200 dark:border-zinc-700 p-5 hover:shadow-xl hover:shadow-slate-200/50 dark:hover:shadow-zinc-800/50 transition-all cursor-pointer relative overflow-hidden"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="space-y-1 min-w-0">
          <span
            className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider ${badgeStyle.bg} ${badgeStyle.text}`}
          >
            {account.currency}
          </span>
          <h4 className="font-bold text-slate-900 dark:text-white leading-snug truncate">
            {account.name}
          </h4>
        </div>
        {account.kind === "digital" ? (
          <div
            className={`size-6 ${DIGITAL_ICON_BG} rounded-full flex items-center justify-center text-white shrink-0`}
          >
            <ArrowLeftRight className="h-3.5 w-3.5" />
          </div>
        ) : (
          <BadgeCheck
            className={`h-5 w-5 shrink-0 ${getIconColorClass(account)}`}
            aria-hidden
          />
        )}
      </div>

      <div className="space-y-3">
        <div className="flex justify-between items-center text-sm gap-2">
          <span className="text-slate-500 dark:text-slate-400 font-medium truncate">
            {account.bankName}
          </span>
          <span className="text-slate-900 dark:text-white font-mono tracking-wider text-xs shrink-0">
            {account.maskedNumber}
          </span>
        </div>
        {account.description && (
          <div className="pt-3 border-t border-slate-100 dark:border-zinc-700">
            <p className="text-xs text-slate-400 dark:text-zinc-500 line-clamp-2 italic">
              &quot;{account.description}&quot;
            </p>
          </div>
        )}
        <div className="flex items-center gap-2 pt-1">
          {account.kind === "digital" && account.extraLabel ? (
            <>
              <Package className="h-3.5 w-3.5 text-slate-400 dark:text-zinc-500 shrink-0" />
              <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                {account.extraLabel}
              </span>
            </>
          ) : (
            <>
              <Paperclip className="h-3.5 w-3.5 text-slate-400 dark:text-zinc-500 shrink-0" />
              <span className="text-[10px] font-bold text-slate-500 dark:text-zinc-400">
                {getAttachmentsLabel(account.attachmentsCount, t)}
              </span>
            </>
          )}
        </div>

        <div className="pt-4 mt-3 border-t border-slate-100 dark:border-zinc-700 flex gap-2">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCardClick?.(account.id);
            }}
            className="flex-1 py-2 px-3 text-sm font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 border border-slate-200 dark:border-zinc-600 rounded-lg transition-colors"
          >
            {t("card_view_button")}
          </button>
          {onEditClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(account.id);
              }}
              className="py-2 px-3 text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-zinc-800 hover:bg-slate-200 dark:hover:bg-zinc-700 border border-slate-200 dark:border-zinc-600 rounded-lg transition-colors flex items-center justify-center gap-1.5 shrink-0"
              title={t("detail_edit_button")}
            >
              <Pencil className="h-4 w-4" />
              {t("detail_edit_button")}
            </button>
          )}
          {onDeleteClick && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(account.id, account.name);
              }}
              className="py-2 px-3 text-sm font-medium text-red-600 dark:text-red-400 bg-slate-100 dark:bg-zinc-800 hover:bg-red-50 dark:hover:bg-red-950/40 border border-slate-200 dark:border-zinc-600 rounded-lg transition-colors flex items-center justify-center shrink-0"
              title={t("card_delete_aria")}
              aria-label={t("card_delete_aria")}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
